import type { ReactNode } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import type { MontajDonusturucuKutusu as DonusturucuVerisi, MontajListeSatiri } from "@oasis/domain";
import { tarihSaat } from "@/lib/veri";
import { Band, Bos, Buton, Etiket, Kutu, Kv, tabloBas, tabloHucre } from "@/components/parcalar";
import { cevir } from "@/lib/dil";
import { dolulukMetni, donusturulenAdi, kuyrukDugmesi, kuyrukEtiketiVar, listeDurumu, satirEtiketleri, tutmaMetni } from "@/lib/montaj";
import { donusturucuRozeti, gecenMetni, saat, sureMetni } from "@/lib/mrb-toplu";
import { KuyrukEtiketi, hataAyrintisi, type IsGorunumu } from "./SokumKuyruk";

/**
 * D-068: Finishing sayfasının montaj kaydı parçaları (söküm listesindeki `SokumKuyruk.tsx`'in eşi). Veri `GET /api/montaj`
 * (`MontajListesi`): "Assembly report converter" kutusu ve "Assembly records" listesi aynı anın verisidir. Bu dosya yalnız çizer:
 * etiketler, durum hücresi, düğme kararı ve cümleler `@/lib/montaj`'daki saf fonksiyonlardadır; kuyruk çağrısı
 * (`montajSiraIslemi`) da oradadır ve sayfa (Assembly.tsx) çağırır.
 */

/**
 * Finishing'deki riser satırının Assembly yuvası (`SetSatiri`'nin `asmYuvasi` alanı): "Assembly" düğmesi riserın Assembly
 * sayfasını açar; yanında riserın etiketleri (`satirEtiketleri`) ve girdisi beklenen kaydın işi varsa kuyruk etiketi. Rapor
 * yükleme, kaldırma ve okunmayan excel eki o sayfadadır. Düğmede yetki koşulu yoktur: sayfayı oturumdaki herkes açar.
 * `kayit` null: riserın montaj kaydı yok (ya da `/montaj` henüz gelmedi; etiketler o zaman yalnız Finishing verisinden kurulur).
 */
export function MontajSatirDugmesi({ rn, gonderildi, asmVar, kayit }: { rn: string; gonderildi: boolean; asmVar: boolean; kayit: MontajListeSatiri | null }) {
  const nav = useNavigate();
  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      <Buton kucuk onClick={() => void nav({ to: "/assembly/$rn", params: { rn } })}>{cevir("Assembly")}</Buton>
      {satirEtiketleri({ gonderildi, asmVar }, kayit).map((e) => <Etiket key={e.metin} tone={e.ton}>{e.metin}</Etiket>)}
      {kayit && kuyrukEtiketiVar(kayit) && <KuyrukEtiketi k={kayit} />}
    </span>
  );
}

/**
 * Assembly girdisinin dönüşüm durumu ve düğmesi (`SokumGirdiDurumu`'nun eşi): queued · {n} (+ Remove from queue) · converting
 * (başlama saati, düğme yok) · failed (+ Retry, altında zaman ve sebep) · none (+ Add to queue, sunuluyorsa). Sökümden farkı:
 * düğmeler `evrak` yetkisi ister ve bekleyen kayıtta da "Remove from queue" sunulur (hiçbir riser bu excel'i beklerken tutulmaz).
 * Düğme kararı `kuyrukDugmesi`'ndedir. `okundu`: girdisi okunmuş kayıt (hatadaki işi "Retry" almaz; riserın Assembly sayfasındaki
 * kuralla aynı). `dugmesiz`: yalnız etiket ve ayrıntı (düğmeleri başka yerde çizen sayfa için).
 */
export function MontajGirdiDurumu({ k, eklenebilir, islem, mesgul, dugmesiz, okundu }: { k: IsGorunumu; eklenebilir: boolean; islem: (islem: "ekle" | "cikar") => void; mesgul?: boolean; dugmesiz?: boolean; okundu?: boolean }) {
  const dugme = dugmesiz ? null : kuyrukDugmesi(k, eklenebilir, okundu);
  const dugmeAdi = dugme === "cikar" ? cevir("Remove from queue") : dugme === "tekrar" ? cevir("Retry") : cevir("Add to queue");
  const ayrinti = k.donusum === "hata" ? hataAyrintisi(k) : "";
  return (
    <>
      <KuyrukEtiketi k={k} />
      {k.donusum === "donusturuluyor" && k.tutmaBaslangic && <> <span className="text-xs text-ink-3">{cevir("since {0}", saat(k.tutmaBaslangic))}</span></>}
      {/* Listede satır tıklaması kaydı açar; düğme açmasın. */}
      {dugme && <> <span onClick={(e) => e.stopPropagation()}><Buton kucuk islem="evrak" mesgul={mesgul} onClick={() => islem(dugme === "cikar" ? "cikar" : "ekle")}>{dugmeAdi}</Buton></span></>}
      {ayrinti && <div className="mt-1 max-w-lg text-xs text-bad">{ayrinti}</div>}
    </>
  );
}

/**
 * D-068 "Assembly report converter" (söküm listesindeki "Disassembly report converter" kutusunun eşi): son bağlantı (sunucu
 * saatiyle), anahtar, şu an dönüştürülen belge ve tutma süresi, sırada ve hatalı Assembly raporu sayısı. Dönüştürücü tektir: başka
 * türde bir belgeyle meşgulse o görünür. Mavi bandın son cümlesi sabittir: dönüştürücü bilgisayar Assembly raporları için
 * kurulana kadar raporlar "queued" bekler.
 */
export function MontajDonusturucuKutusu({ d, simdi }: { d: DonusturucuVerisi; simdi: number }) {
  const r = donusturucuRozeti(d, simdi);
  const rozet =
    r.tur === "bagli" ? <Etiket tone="ok">{cevir("connected")}</Etiket>
    : r.tur === "kopuk" ? <Etiket tone={r.uyari ? "warn" : "neutral"}>{cevir("not connected for {0}", sureMetni(r.gecen ?? 0))}</Etiket>
    : <Etiket>{cevir("not connected yet")}</Etiket>;
  const ad = donusturulenAdi(d.donusturulen);
  const tutma = tutmaMetni(d.donusturulen);
  return (
    <Kutu baslik={cevir("Assembly report converter")} aksiyon={rozet}>
      <div className="grid gap-x-4 gap-y-3 sm:grid-cols-2 lg:grid-cols-4">
        <Kv ad={cevir("Last connected")}>{d.sonBaglanti ? <><b>{tarihSaat(d.sonBaglanti)}</b> <span className="text-ink-3">· {gecenMetni(r.gecen ?? 0)}</span></> : cevir("never")}</Kv>
        <Kv ad={cevir("Key")}>{d.anahtarAd ? <>{d.anahtarAd} <span className="text-ink-3">· {cevir("created by System Administrator")}</span></> : "—"}</Kv>
        <Kv ad={cevir("Now converting")}>
          {ad ? <><b className="font-mono">{ad}</b>{tutma && <> <span className="text-ink-3">· {tutma}</span></>}</> : "—"}
        </Kv>
        <Kv ad={cevir("Waiting")}><b className="tnum">{d.sirada}</b> {cevir("queued")} <span className="text-ink-3">·</span> <b className="tnum">{d.hatali}</b> {cevir("failed")}</Kv>
      </div>
      {!d.aktifAnahtarVar && <Band tip="not" className="mt-3">{cevir("No active converter key. A System Administrator creates one in Admin.")}</Band>}
      {r.uyari && <Band tip="not" className="mt-3">{cevir("The converter has not connected for {0} and {1} Assembly reports are waiting. The converter computer may be off or asleep.", sureMetni(r.gecen ?? 0), d.sirada)}</Band>}
      <Band tip="bilgi" className="mt-3">{cevir("The converter runs on a computer outside OASIS. While that computer is on, it checks the queue every 15 minutes and converts one document at a time; Assembly reports are taken last, after Disassembly reports, MRBs, Disposition reports and Rework reports. It reads the scanned form with the assembly engine and delivers the assembly input excel. The excel is read into the assembly record right away, with the same checks as a manual upload. An excel that fails the checks or comes with an engine warning is not read and the record shows as failed, with the reason. An assembly input uploaded by hand is never replaced by the converter. Nothing in the Finishing step waits for this excel. If the computer sleeps during a conversion, the report goes back to the queue when its hold time runs out.")} {cevir("Assembly reports are converted only after the converter computer is set up for them.")}</Band>
    </Kutu>
  );
}

/**
 * "Status" hücresi (`listeDurumu`): kaydın hâl etiketi; okunmuş kayıtta girdinin kaynağı; iş durumu ve düğmesi etiketin yanında
 * (girdisi beklenen kayıt) ya da "assembly input" sözüyle alt satırda (yeniden sıraya konmuş okunmuş kayıt, girdisiz kayıt);
 * girdisiz kayıtta işi yokken gri not; arşiv kaydı (gri) ve başka dosyanın kaydı (sarı) ek satırı.
 */
function KayitDurumu({ s, mesgul, islem }: { s: MontajListeSatiri; mesgul: boolean; islem: (islem: "ekle" | "cikar") => void }) {
  const d = listeDurumu(s);
  const kuyruk = <MontajGirdiDurumu k={s} eklenebilir={s.kuyrugaEklenebilir} okundu={s.hal === "okundu"} mesgul={mesgul} islem={islem} />;
  const gri = (metin: string) => <div className="mt-1 text-xs text-ink-3">{metin}</div>;
  return (
    <>
      <Etiket tone={d.etiket.ton}>{d.etiket.metin}</Etiket>
      {d.kaynak && <> <span className="text-xs text-ink-3">{d.kaynak}</span></>}
      {d.kuyruk === "etiketle" && <> {kuyruk}</>}
      {d.kuyruk === "alt_satir" && <div className="mt-1"><span className="text-xs text-ink-3">{cevir("assembly input")}</span> {kuyruk}</div>}
      {d.girdisizNotu && gri(cevir("completed without the assembly input"))}
      {d.uyum && (d.uyum.ton === "neutral" ? gri(d.uyum.metin) : <div className="mt-1"><Etiket tone={d.uyum.ton}>{d.uyum.metin}</Etiket></div>)}
    </>
  );
}

/**
 * D-068 "Assembly records" (söküm listesindeki "Disassembly records" kutusunun eşi): riser başına bir montaj kaydı. Finishing
 * listesinde olmayan arşiv riserlarını da taşıdığı için ayrı kutudur. Satıra tıklamak riserın Assembly sayfasını açar; hücredeki
 * düğmeler tıklamayı durdurur. "Skip" ve "+ Start new" karşılığı yoktur: yeni montaj riser satırındaki "Assembly" düğmesinden
 * başlar. `siraMesgul`: kuyruk işlemi süren kaydın kimliği. `children`: kutunun altı (yükleme düğmesi ve açıklama bandı).
 */
export function MontajKayitListesi({ kayitlar, siraMesgul, sira, children }: { kayitlar: MontajListeSatiri[]; siraMesgul: string | null; sira: (id: string, islem: "ekle" | "cikar") => void; children?: ReactNode }) {
  const nav = useNavigate();
  return (
    <Kutu baslik={cevir("Assembly records")} padded={false}>
      <div className="scroll-thin overflow-x-auto">
        <table className="w-full min-w-[640px]">
          <thead><tr className="border-b border-line"><th className={tabloBas}>RN</th><th className={tabloBas}>{cevir("Form fullness")}</th><th className={tabloBas}>{cevir("Status")}</th><th /></tr></thead>
          <tbody>
            {kayitlar.map((s) => (
              <tr key={s.id} className="cursor-pointer border-b border-line last:border-0 hover:bg-surface-2" onClick={() => void nav({ to: "/assembly/$rn", params: { rn: s.rn } })}>
                <td className={tabloHucre}><b className="font-mono">{s.rn}</b></td>
                <td className={tabloHucre}>{dolulukMetni(s)}</td>
                <td className={tabloHucre}><KayitDurumu s={s} mesgul={siraMesgul === s.id} islem={(i) => sira(s.id, i)} /></td>
                <td className={tabloHucre}><Link to="/assembly/$rn" params={{ rn: s.rn }} className="text-sm font-semibold text-orange">{cevir("Open record")}</Link></td>
              </tr>
            ))}
            {!kayitlar.length && <tr><td colSpan={4}><Bos>{cevir("No assembly records yet.")}</Bos></td></tr>}
          </tbody>
        </table>
      </div>
      {children}
    </Kutu>
  );
}
